import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Fingerprint, KeyRound, Trash2 } from "lucide-react";
import { api, ApiError, okSchema } from "@/lib/api";
import {
  createPasskey,
  passkeySupported,
  PasskeyError,
  type PublicKeyCredentialCreationOptionsJSON,
} from "@/lib/webauthn";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Alert, EmptyState, LoadingPanel } from "@/components/ui/Feedback";
import { formatDate } from "@/components/ui/DataTable";
import { useToast } from "@/components/ui/toast";

const passkeyListSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        device_name: z.string(),
        transports: z.array(z.string()).default([]),
        created_at: z.string().nullable().default(null),
        last_used_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

// py_webauthn returns the raw options object, not a wrapper.
const creationOptionsSchema = z.looseObject({
  challenge: z.string(),
  rp: z.looseObject({ name: z.string() }),
  user: z.looseObject({ id: z.string(), name: z.string(), displayName: z.string() }),
  pubKeyCredParams: z.array(z.looseObject({ alg: z.number() })).default([]),
});

function defaultDeviceName() {
  const agent = navigator.userAgent;
  if (/iPhone|iPad/.test(agent)) return "iPhone / iPad";
  if (/Android/.test(agent)) return "Perangkat Android";
  if (/Mac/.test(agent)) return "Mac";
  if (/Windows/.test(agent)) return "Windows";
  return "Perangkat ini";
}

/**
 * Passkey registration and revocation.
 *
 * The server only ever stores a public key credential — no fingerprint or face
 * data leaves the device, which is what makes this safe to offer patients.
 */
export function PasskeyManager() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const supported = passkeySupported();

  const { data, isPending, error: loadError } = useQuery({
    queryKey: ["patient", "passkeys"],
    queryFn: ({ signal }) => api.get("/api/patient/passkeys", passkeyListSchema, signal),
  });

  const register = useMutation({
    mutationFn: async () => {
      const options = await api.post<PublicKeyCredentialCreationOptionsJSON>(
        "/api/passkeys/register/options",
        creationOptionsSchema as unknown as z.ZodType<PublicKeyCredentialCreationOptionsJSON>,
        {},
      );
      const credential = await createPasskey(options);
      return api.post("/api/passkeys/register/verify", okSchema, {
        credential,
        device_name: defaultDeviceName(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["patient", "passkeys"] });
      setError(null);
      toast.success({
        title: "Passkey terdaftar",
        description: "Lain kali Anda dapat masuk tanpa mengetik password.",
      });
    },
    onError: (cause) => {
      setError(
        cause instanceof PasskeyError || cause instanceof ApiError
          ? cause.message
          : "Passkey gagal didaftarkan.",
      );
    },
  });

  const revoke = useMutation({
    mutationFn: (id: number) =>
      api.post(`/patient/security/passkeys/${id}/revoke`, okSchema, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["patient", "passkeys"] });
      toast.success({ title: "Passkey dicabut" });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal mencabut passkey",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  return (
    <Card>
      <CardHeader
        eyebrow="Masuk tanpa password"
        title="Passkey"
        description="Gunakan sidik jari, Face ID, atau Windows Hello untuk masuk."
        action={
          supported ? (
            <Button
              size="sm"
              variant="primary"
              icon={<Fingerprint className="size-4" />}
              loading={register.isPending}
              onClick={() => register.mutate()}
            >
              Tambah
            </Button>
          ) : undefined
        }
      />

      <CardBody className="space-y-4">
        {!supported && (
          <Alert tone="info" title="Passkey belum tersedia di sini">
            Browser ini tidak mendukung passkey, atau halaman tidak diakses melalui HTTPS.
            Anda tetap dapat masuk menggunakan password.
          </Alert>
        )}

        {error && <Alert tone="danger">{error}</Alert>}

        {loadError && (
          <Alert tone="danger">
            {loadError instanceof ApiError ? loadError.message : "Gagal memuat daftar passkey."}
          </Alert>
        )}

        <p className="text-content-subtle text-xs">
          Server hanya menyimpan kunci publik perangkat Anda. Data sidik jari atau wajah tidak
          pernah dikirim ke Mediora.
        </p>
      </CardBody>

      {isPending ? (
        <LoadingPanel />
      ) : data && data.items.length > 0 ? (
        <ul className="divide-hairline border-hairline divide-y border-t">
          {data.items.map((item) => (
            <li key={item.id} className="flex items-center gap-3 px-5 py-3.5">
              <KeyRound aria-hidden className="text-content-subtle size-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{item.device_name}</p>
                <p className="text-content-subtle truncate text-xs">
                  Dibuat {formatDate(item.created_at)}
                  {item.last_used_at && ` · terakhir dipakai ${formatDate(item.last_used_at)}`}
                </p>
              </div>
              {item.transports.length > 0 && (
                <Badge className="hidden sm:inline-flex">{item.transports.join(", ")}</Badge>
              )}
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Cabut passkey ${item.device_name}`}
                loading={revoke.isPending && revoke.variables === item.id}
                onClick={() => revoke.mutate(item.id)}
                icon={<Trash2 className="size-3.5" />}
              />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={<Fingerprint className="size-5" />}
          title="Belum ada passkey"
          description="Tambahkan perangkat ini agar bisa masuk tanpa mengetik password."
        />
      )}
    </Card>
  );
}
